'use client';

import { CloseIcon } from '@/components/icons/close-icon';
import { Badge } from '@/components/ui/badge';
import { useDataAnalytics } from '@/hooks/analytics/analytics';

type MoviesActiveFiltersProps = {
  activeFilters: string[];
  onClearZipCode: () => void;
  onRemoveFilter: (filter: string) => void;
  zipCode?: string;
};

export const MoviesActiveFilters = ({
  activeFilters,
  onClearZipCode,
  onRemoveFilter,
  zipCode,
}: Readonly<MoviesActiveFiltersProps>): React.JSX.Element | null => {
  const { trackClick } = useDataAnalytics();

  if (!zipCode && !activeFilters.length) return null;

  const handleClear = (text: string, onClear: () => void): void => {
    trackClick({ linkCategory: 'Movies', linkName: 'Clear' });
    onClear();
  };

  const renderChip = (text: string, onClear: () => void): React.JSX.Element => {
    return (
      <Badge className="gap-sm"
        variant="chip"
      >
        {text}
        <button aria-label={`Clear ${text}`}
          className="cursor-pointer rounded-full border-0 bg-[var(--color-grey-600)] p-[3px]
            hover:bg-[var(--color-grey-700)] focus-visible:outline-2 focus-visible:outline-offset-2
            focus-visible:outline-[var(--primary)]"
          data-test="button-clear-chip"
          type="button"
          onClick={(): void => handleClear(text, onClear)}
        >
          <CloseIcon aria-hidden="true"
            className="h-2 w-2 text-white"
          />
        </button>
      </Badge>
    );
  };

  const getFilterDisplayName = (filter: string): string => {
    if (filter === 'sort-release-date') return 'Release Date';
    if (filter === 'sort-alphabetical') return 'Alphabetical';
    return filter.replace(/^(format|genre|language|rating)-/, '');
  };

  return (
    <div className="flex flex-wrap gap-2"
      data-test="movies-active-filters"
    >
      {!!zipCode &&
        renderChip(`Near ${zipCode}`, onClearZipCode)
      }
      {activeFilters.map(filter =>
        <div key={filter}>
          {renderChip(getFilterDisplayName(filter), (): void => onRemoveFilter(filter))}
        </div>
      )}
    </div>
  );
};
